<template>
  <div class="sib-color-picker">
    <div class="sib__option-colors">
      <template v-for="(color, i) in colors" :key="i">
        <label
          class="sib__option-color sib__option-color--round"
          :style="buildOptionColor(color)"
          :for="`${id}-${i}`"
        >
          <input
            type="color"
            :id="`${id}-${i}`"
            :value="color"
            @input="changeColor($event, i)"
          />
          <div
            v-if="colors.length > 1"
            class="sib__trash-icon"
            @click.prevent="deleteColorOption(i)"
          >
            <TrashIcon />
          </div>
        </label>
      </template>
      <label
        v-if="type == MULTIPLE_TYPE && colors.length < maxColorOption"
        class="sib__option-color-more sib__option-color--round"
        @click="addColorOption(colors.length)"
      >
        <PlusIcon />
      </label>
    </div>

    <template v-if="!hideProperties">
      <input type="hidden" :name="`properties[${id}]`" :value="`Colors: `" />
      <input
        type="hidden"
        :name="`properties[${
          attributeKey
            ? attributeKey + CUSTOM_COLOR_PICKER_PREFIX
            : CUSTOM_COLOR_PICKER_PREFIX
        } ${id}]`"
        :value="colors.join()"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import TrashIcon from '@/assets/images/icons/trash-icon.svg'
import PlusIcon from '@/assets/images/icons/plus-icon.svg'
import { CUSTOM_COLOR_PICKER_PREFIX, cloneDeep } from '@/utils'

interface ColorPickerProps {
  value?: string
  type?: string
  id?: string
  hideProperties?: boolean
  attributeKey?: string
}

const props = withDefaults(defineProps<ColorPickerProps>(), {
  value: '#000000',
  type: 'single',
  id: 'sib',
  hideProperties: true,
})

const emit = defineEmits<{
  (e: 'change-colors', colors: string[]): void
}>()

const colors = ref([props.value || '#000000'])
const maxColorOption = 5
const MULTIPLE_TYPE = 'multiple'

function buildOptionColor(color: string) {
  if (color) return `background:${color}`
  return `background:${props.value}`
}

function changeColor(event: Event, index: number) {
  const color = (event.target as HTMLInputElement)?.value
  colors.value.splice(index, 1, color)
  emit('change-colors', cloneDeep(colors.value))
}

function addColorOption(index: number) {
  colors.value.splice(index, 0, props.value)
  emit('change-colors', cloneDeep(colors.value))
}

function deleteColorOption(index: number) {
  colors.value.splice(index, 1)
  emit('change-colors', cloneDeep(colors.value))
}
</script>

<style lang="scss">
.sib {
  &__option-colors {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em 1em;
    margin-top: 0.5rem;
  }
  &__option-color {
    width: var(--product-option-choice-height) !important;
    height: var(--product-option-choice-height) !important;
    position: relative;
    padding: 0 18px;
    display: flex;
    height: var(--product-option-choice-height);
    justify-content: center;
    align-items: center;
    border: solid 1px currentColor;
    border-radius: var(--border-radius-medium);
    font-weight: bold;
    color: var(--text-color-secondary);
    letter-spacing: -0.75px;
    cursor: pointer;
    $optionChoice: &;

    input[type='color'] {
      width: var(--product-option-choice-height);
      height: var(--product-option-choice-height);
      opacity: 0;
      z-index: -1;
      &:focus {
        & + .sib__trash-icon {
          background: #ffffff;
          opacity: 1;
          z-index: var(--base-z-index);
        }
      }
    }
    &--round {
      --round-option-border-bonus-size: 8px;
      width: var(--product-option-choice-height);
      height: var(--product-option-choice-height);
      border-radius: 50%;
      padding: 0;
      font-size: 0;
      line-height: 0;
      border-color: var(--border-color);
      position: relative;
      &:hover {
        &::after {
          content: ' ';
          display: block;
          position: absolute;
          width: calc(
            var(--product-option-choice-height) +
              var(--round-option-border-bonus-size)
          );
          height: calc(
            var(--product-option-choice-height) +
              var(--round-option-border-bonus-size)
          );
          top: calc(var(--round-option-border-bonus-size) / -2 - 1px);
          left: calc(var(--round-option-border-bonus-size) / -2 - 1px);
          border: solid 2px var(--border-color-box);
          border-radius: 50%;
        }
      }
    }
  }
  &__option-color-more {
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;
    width: var(--product-option-choice-height) !important;
    height: var(--product-option-choice-height) !important;
    border-radius: 50%;
    border: 1px solid var(--border-color);
    &:hover {
      cursor: pointer;
    }
  }
  &__trash-icon {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 23.5px;
    height: 23.5px;
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.3s;
    svg {
      width: 15px;
    }
  }
}
label.sib__option-color,
label.sib__option-color-more {
  margin: 0 !important;
}
</style>
